import {
  useEffect,
  useRef,
  useState } from "react";
import { contentBase,
  adminApi,
  type ManifestEntry } from "../lib/contentApi";
import { stagePosForWidth,
  landingCharHome,
  LANDING_BG_HOME,
} from "@greg-chick/shared";

const clamp = (n: number) => Math.max(0, Math.min(1, n));

// 에셋이 "실제 어디에 어떻게" 반영되는지 맥락 미리보기(FR-ADMIN-05 강화).
// 슬롯키 → 컨텍스트 추론 후 대표 화면 목업에 업로드 이미지를 얹어 보여줌.

const OBJ_EMOJI: Record<string, string> = {
  portal: "🚪", npc: "🐤", video: "📺", link: "🔗", card: "📖", store: "🛍️",
};

interface TiledLike {
  width: number;
  height: number;
  tilewidth?: number;
  layers: { type: string; objects?: { x: number; y: number; width?: number; height?: number; type?: string }[] }[];
}

async function fetchMap(zone: string): Promise<TiledLike | null> {
  for (const url of [`${adminApi()}/api/map/${zone}`, `${contentBase()}/maps/${zone}.json`]) {
    try {
      const r = await fetch(url, { cache: "no-cache" });
      if (r.ok) return (await r.json()) as TiledLike;
    } catch {
      /* 다음 */
    }
  }
  return null;
}

const DIR_LABEL: Record<string, string> = { down: "앞(정면)", up: "뒤", side: "옆" };

export function ContextPreview({
  slotKey,
  url,
  entry,
  onChange,
}: {
  slotKey: string;
  url: string;
  entry?: ManifestEntry;
  onChange?: (key: string, entry: ManifestEntry) => void;
}) {
  const base = slotKey.replace(/\.[^.]+$/, ""); // world.background.lobby → world.background
  const tail = slotKey.split(".").pop() ?? ""; // lobby / p01 / greg_cat / default / down

  // 메인(랜딩) 캐릭터·배경 — 실제 화면 맥락 위에서 드래그로 위치 변경
  if ((base === "landing.char" || base === "landing.hero") && entry && onChange) {
    return <LandingEdit slotKey={slotKey} entry={entry} onChange={onChange} isBg={base === "landing.hero"} />;
  }

  // 방향별 걷기 스트립(avatar.player.down, npc.greg_cat.side …)
  if (tail === "down" || tail === "up" || tail === "side") {
    return <StripPreview url={url} dir={tail} />;
  }
  // 히어로 파츠(hero.body.b2 …) — 100×100 레이어
  if (base.startsWith("hero.")) {
    return (
      <FramePreview url={url} ratio="1 / 1" caption={`히어로 ${base.slice(5)} 레이어(${tail}) — 아바타 위에 같은 100×100 캔버스로 겹쳐집니다`} />
    );
  }
  if (base === "world.background") return <ZoneBg zone={tail} url={url} />;
  if (base === "world.tileset" || base === "world.wall")
    return <TilePreview url={url} label={base === "world.wall" ? "벽 타일" : "바닥 타일"} zone={tail} />;
  if (base === "npc") return <NpcPreview url={url} id={tail} />;
  if (base === "book.page") return <FramePreview url={url} ratio="3 / 4" caption={`전자책 ${tail}쪽으로 보입니다`} />;
  if (base === "landing.hero") return <FramePreview url={url} ratio="16 / 7" caption="랜딩 상단 배경으로 보입니다" />;
  if (base === "event.banner") return <BannerPreview url={url} />;
  if (base === "recommend.image") return <FramePreview url={url} ratio="1 / 1" caption="굿즈 추천컷으로 보입니다" />;
  if (base === "ar.poster") return <PhonePreview url={url} />;
  return <FramePreview url={url} ratio="4 / 3" caption="이 이미지로 교체됩니다" />;
}

/** 존 배경 + 실제 오브젝트 위치 오버레이 — "이 위치들에 배치됩니다". */
function ZoneBg({ zone, url }: { zone: string; url: string }) {
  const [map, setMap] = useState<TiledLike | null>(null);
  useEffect(() => {
    void fetchMap(zone).then(setMap);
  }, [zone]);

  const cols = map?.width ?? 13;
  const rows = map?.height ?? 9;
  const tile = map?.tilewidth ?? 48;
  const W = cols * tile;
  const H = rows * tile;
  const og = map?.layers.find((l) => l.type === "objectgroup");

  return (
    <div className="cp">
      <p className="cp__cap">
        <b>{zone}</b> 존 전체 배경 — 아래 위치에 오브젝트가 얹힙니다
      </p>
      <div className="cp__frame" style={{ aspectRatio: `${cols} / ${rows}` }}>
        <img className="cp__img" src={url} alt="" />
        {og?.objects?.map((o, i) => {
          const cx = (o.x + (o.width ?? tile) / 2) / W;
          const cy = (o.y + (o.height ?? tile) / 2) / H;
          return (
            <span
              key={i}
              className="cp__marker"
              style={{ left: `${cx * 100}%`, top: `${cy * 100}%` }}
            >
              {OBJ_EMOJI[o.type ?? ""] ?? "•"}
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** 48px 타일이 반복 적용된 모습(4×4). */
function TilePreview({ url, label, zone }: { url: string; label: string; zone: string }) {
  return (
    <div className="cp">
      <p className="cp__cap">
        <b>{zone}</b> 존 {label} — 이렇게 반복(타일링)됩니다
      </p>
      <div className="cp__tilegrid">
        {Array.from({ length: 16 }, (_, i) => (
          <img key={i} className="cp__tile" src={url} alt="" />
        ))}
      </div>
    </div>
  );
}

/** NPC 캐릭터가 바닥에 선 모습. */
function NpcPreview({ url, id }: { url: string; id: string }) {
  return (
    <div className="cp">
      <p className="cp__cap">
        NPC <code>npc_sprite={id}</code> 로 지정된 위치에 이렇게 등장합니다
      </p>
      <div className="cp__npcstage">
        <img className="cp__npc" src={url} alt="" />
      </div>
    </div>
  );
}

/** 메인 화면 위치 편집 — 실제 무대 목업 위에서 캐릭터/배경을 드래그로 배치 + 크기. */
function LandingEdit({
  slotKey,
  entry,
  onChange,
  isBg,
}: {
  slotKey: string;
  entry: ManifestEntry;
  onChange: (key: string, e: ManifestEntry) => void;
  isBg: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(false);
  /**
   * 📱 **좁은 화면 자리를 따로 정한다.**
   *
   * 자리는 무대 폭의 **비율**이라 화면이 좁아져도 상대 위치가 그대로다 — 넓은 화면에서 왼쪽에
   * 나란히 둔 고양이·병아리가 폰에서는 **왼쪽에 겹쳐 몰리고 오른쪽이 텅 빈다.**
   * 그래서 폰용 자리를 따로 둔다(`posNarrow`). 배경은 화면을 꽉 채우므로 이 구분이 필요 없다.
   *
   * 안 정해 두면 아이 화면은 기본 규칙(`stagePosForWidth` — 병아리를 히어로 오른쪽으로)을 쓴다.
   * 즉 **아무것도 안 해도 폰에서 안 몰리고**, 손대고 싶을 때만 여기서 정한다.
   *
   * 🔴 **그 기본 규칙을 미리보기도 써야 한다.** 예전에는 여기서만 「폰 자리를 안 정했으면
   * 넓은 화면 자리를 그대로」 보여 줬다. 그래서 병아리를 왼쪽에 둔 운영자가 📱 탭을 열면
   * **둘이 겹쳐 보였다** — 실제 아이 폰에서는 규칙이 병아리를 오른쪽으로 옮겨 멀쩡한데.
   * 운영자는 멀쩡한 것을 고치려 들거나 이 기능이 고장 났다고 여긴다.
   * 판정은 shared 한 곳(`stagePosForWidth`) — 아이 화면과 **같은 함수**를 부른다.
   */
  const [narrow, setNarrow] = useState(false);
  // 배경은 화면을 꽉 채우므로 좁은 화면 규칙이 없다(id 를 넘겨도 그대로 돌아온다)
  const charId = slotKey.replace(/^landing\.char\./, "");
  /**
   * 🔴 **기본 자리도 아이 화면과 같은 답을 봐야 한다.**
   * 예전엔 여기만 0.45/0.72 로 적어 둬서, 자리를 안 정한 새 그림이
   * 미리보기와 실제 화면에서 **다른 자리**에 섰다.
   */
  const wide = entry.pos ?? (isBg ? LANDING_BG_HOME : landingCharHome(charId));
  const pos = isBg
    ? narrow
      ? (entry.posNarrow ?? wide)
      : wide
    : stagePosForWidth(charId, wide, narrow, entry.posNarrow);
  const scale = entry.scale ?? 1;

  function moveTo(clientX: number, clientY: number) {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    const next = { x: clamp((clientX - r.left) / r.width), y: clamp((clientY - r.top) / r.height) };
    onChange(slotKey, narrow ? { ...entry, posNarrow: next } : { ...entry, pos: next });
  }

  return (
    <div className="cp">
      <p className="cp__cap">
        {isBg ? "메인 무대 배경" : "메인 화면 캐릭터"} — 실제 화면 위에서 <b>드래그로 위치</b>를 옮기세요.
      </p>

      {/* 배경은 화면을 꽉 채우니 넓고 좁고를 나눌 이유가 없다 — 캐릭터에만 보여준다 */}
      {!isBg ? (
        <div className="cp-modes" role="group" aria-label="화면 폭">
          <button
            type="button"
            className={`cp-modes__btn${narrow ? "" : " is-on"}`}
            onClick={() => setNarrow(false)}
          >
            🖥 넓은 화면
          </button>
          <button
            type="button"
            className={`cp-modes__btn${narrow ? " is-on" : ""}`}
            onClick={() => setNarrow(true)}
          >
            📱 좁은 화면(폰)
          </button>
          {narrow ? (
            <span className="cp-modes__hint">
              {entry.posNarrow ? "폰 전용 자리를 쓰는 중" : "아직 안 정했어요 — 여기서 옮기면 폰에만 적용돼요"}
            </span>
          ) : null}
        </div>
      ) : null}

      <div
        ref={ref}
        className={`cp-stage${narrow ? " cp-stage--narrow" : ""}`}
        onPointerMove={(e) => drag && moveTo(e.clientX, e.clientY)}
        onPointerUp={() => setDrag(false)}
        onPointerLeave={() => setDrag(false)}
      >
        {/* 실제 메인 화면 요소(목업): 배너 · 우측 메뉴 · CTA */}
        {isBg ? (
          <img
            className="cp-stage__bg"
            src={entry.url}
            alt=""
            style={{ objectPosition: `${pos.x * 100}% ${pos.y * 100}%` }}
            onPointerDown={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
          />
        ) : null}
        <div className="cp-stage__banner">경주 그림책방</div>
        <div className="cp-stage__menu">
          {["책 속으로", "이벤트", "굿즈 마켓", "북토크"].map((t) => (
            <span key={t}>{t}</span>
          ))}
        </div>
        <div className="cp-stage__cta">체험 공간 입장 ▶️</div>
        {!isBg ? (
          <img
            className="cp-stage__char"
            src={entry.url}
            alt=""
            draggable={false}
            style={{ left: `${pos.x * 100}%`, top: `${pos.y * 100}%`, transform: `translate(-50%,-50%) scale(${scale})` }}
            onPointerDown={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
          />
        ) : null}
      </div>
      <div className="cp-tools">
        <label>
          크기
          <input
            type="range"
            min={0.5}
            max={1.8}
            step={0.05}
            value={scale}
            onChange={(e) => onChange(slotKey, { ...entry, scale: Number(e.target.value) })}
          />
          {scale.toFixed(2)}×
        </label>
        {narrow && entry.posNarrow ? (
          <button
            type="button"
            onClick={() => {
              const { posNarrow: _drop, ...rest } = entry;
              onChange(slotKey, rest);
            }}
          >
            폰 전용 자리 지우기
          </button>
        ) : null}
        <button type="button" onClick={() => onChange(slotKey, { url: entry.url, alt: entry.alt })}>
          위치·크기 초기화
        </button>
      </div>
    </div>
  );
}

/** 방향별 걷기 스트립 — 가로 스트립 전체 + 안내. */
function StripPreview({ url, dir }: { url: string; dir: string }) {
  return (
    <div className="cp">
      <p className="cp__cap">
        <b>{DIR_LABEL[dir]}</b> 걷기 스트립 — 왼쪽부터 <b>대기 → 걷기</b> 순서로 이어집니다(각 칸 정사각형).
        {dir === "side" ? " 왼쪽 방향은 자동 좌우반전됩니다." : ""}
      </p>
      <div className="cp__strip">
        <img src={url} alt="" />
      </div>
    </div>
  );
}

function FramePreview({ url, ratio, caption }: { url: string; ratio: string; caption: string }) {
  return (
    <div className="cp">
      <p className="cp__cap">{caption}</p>
      <div className="cp__frame" style={{ aspectRatio: ratio }}>
        <img className="cp__img" src={url} alt="" />
      </div>
    </div>
  );
}

function BannerPreview({ url }: { url: string }) {
  return (
    <div className="cp">
      <p className="cp__cap">랜딩 상단 이벤트 배너로 보입니다</p>
      <div className="cp__landing">
        <div className="cp__banner">
          <img className="cp__img" src={url} alt="" />
        </div>
        <div className="cp__landing-body">콘텐츠 영역…</div>
      </div>
    </div>
  );
}

function PhonePreview({ url }: { url: string }) {
  return (
    <div className="cp">
      <p className="cp__cap">AR 장면 포스터로 보입니다</p>
      <div className="cp__phone">
        <div className="cp__frame" style={{ aspectRatio: "9 / 16" }}>
          <img className="cp__img" src={url} alt="" />
        </div>
      </div>
    </div>
  );
}
